<template>
  <div id="reviews-section" class="reviews-section profile__section">
    <div class="reviews-section__header">
      <div class="profile__section__title">Reviews</div>
      <select
        v-model="sortBy"
        class="reviews-section__sort"
        aria-label="Sort reviews"
      >
        <option value="newest">Newest</option>
        <option value="oldest">Oldest</option>
        <option value="rating_desc">Rating: high to low</option>
        <option value="rating_asc">Rating: low to high</option>
      </select>
    </div>

    <div class="reviews-section__cards">
      <template v-for="(review, idx) in paginatedReviews" :key="idx">
        <hr v-if="idx > 0" class="reviews-section__divider" />
        <review-card
          :author="review.author"
          :rating="review.rating"
          :text="review.text"
          :date="review.date"
          :source="review.source"
        />
      </template>
    </div>

    <review-pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="onPageChange"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ReviewCard from './review-card.vue';
import ReviewPagination from './review-pagination.vue';

const props = defineProps({
  reviews: {
    type: Array,
    required: true,
  },
  pageSize: {
    type: Number,
    default: 5,
  },
});

const sortBy = ref('newest');
const currentPage = ref(1);

const sortedReviews = computed(() => {
  const reviews = [...props.reviews];

  switch (sortBy.value) {
    case 'newest':
      return reviews.sort((a, b) => new Date(b.date) - new Date(a.date));
    case 'oldest':
      return reviews.sort((a, b) => new Date(a.date) - new Date(b.date));
    case 'rating_desc':
      return reviews.sort((a, b) => b.rating - a.rating || new Date(b.date) - new Date(a.date));
    case 'rating_asc':
      return reviews.sort((a, b) => a.rating - b.rating || new Date(b.date) - new Date(a.date));
    default:
      return reviews;
  }
});

const totalPages = computed(() => Math.ceil(sortedReviews.value.length / props.pageSize));

const paginatedReviews = computed(() => {
  const start = (currentPage.value - 1) * props.pageSize;
  return sortedReviews.value.slice(start, start + props.pageSize);
});

function onPageChange(page) {
  currentPage.value = page;
}

watch(sortBy, () => {
  currentPage.value = 1;
});
</script>

<style lang="scss" scoped>
.reviews-section {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: calc(var(--grid-unit) * 2);
  }

  &__sort {
    border: 1px solid var(--gray-500);
    border-radius: 4px;
    margin-top: 50px;
    padding: 8px 8px;
    font-size: var(--font-size-14);
    color: var(--gray-almostBlack);
    background: #fff;
    cursor: pointer;

    &:focus {
      outline: 2px solid var(--sapphire-700);
      outline-offset: 1px;
    }
  }

  &__divider {
    border: none;
    border-top: 1px solid #d1d5db;
    margin: calc(var(--grid-unit) * 2) 0;
  }
}
</style>
